/**
 * Format a timestamp as relative time (e.g., "2h ago", "5m ago")
 *
 * @param {number|string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted relative time string
 */
export function formatTimeAgo(timestamp) {
  if (!timestamp) return '';

  const now = Date.now();
  const nowDate = new Date(now);
  const then = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  const thenDate = new Date(then);
  const diffInSeconds = Math.floor((now - then) / 1000);

  // Just now (< 1 minute)
  if (diffInSeconds < 60) {
    return 'Just now';
  }

  // Minutes ago (< 1 hour)
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  // Hours ago (< 24 hours)
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  // Days ago (< 7 days)
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  // Calendar-based month difference
  const diffInMonths = (nowDate.getFullYear() - thenDate.getFullYear()) * 12
    + (nowDate.getMonth() - thenDate.getMonth());

  // Days ago (< 1 month)
  if (diffInMonths < 1 || diffInDays < 30) {
    return `${diffInDays}d ago`;
  }

  // Months ago (< 12 months)
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }

  // Years ago
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears}y ago`;
}

/**
 * Smart date display: relative time for recent items, absolute date for older ones.
 * - < 7 days: relative time ("2h ago", "3d ago")
 * - 7+ days, current year: "Mar 15"
 * - Different year: "Mar 15, 2024"
 *
 * @param {number|string|Date} timestamp - The timestamp to format
 * @returns {string} Smart formatted date string
 */
export function formatSmartDate(timestamp) {
  if (!timestamp) return '';

  const now = Date.now();
  const then = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  const diffInDays = Math.floor((now - then) / 1000 / 60 / 60 / 24);

  // Recent: show relative time
  if (diffInDays < 7) {
    return formatTimeAgo(timestamp);
  }

  // Older: show absolute date (year-smart)
  return formatFullDate(timestamp);
}

/**
 * Format a timestamp as a full date (e.g., "Oct 25, 2024")
 * 
 * @param {number|string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted date string
 */
export function formatFullDate(timestamp) {
  if (!timestamp) return '';

  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();

  const options = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString('en-US', options);
}

/**
 * Format a timestamp as date with time (e.g., "Oct 25, 2024 at 2:30 PM")
 * 
 * @param {number|string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted date and time string
 */
export function formatFullDateTime(timestamp) {
  if (!timestamp) return '';

  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();

  const dateOptions = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    dateOptions.year = 'numeric';
  }

  const dateStr = date.toLocaleDateString('en-US', dateOptions);

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return `${dateStr} at ${timeStr}`;
}
